import {
  Category,
  Data2,
  EmojiHappy,
  Flash,
  Message,
  ProfileCircle,
} from 'iconsax-react';
import type { Icon } from 'iconsax-react';
import type { ComponentType, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@design-system/helpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system/tooltip';
import { createRoute, PATHS } from '@src/core/router/paths';

import { BrainIcon } from '../../icons';

type OmniIcon = Icon | ComponentType<{ size?: number; className?: string }>;

type OmniTab = {
  id: string;
  label: string;
  icon?: OmniIcon;
  /** App path (no org slug). Tabs without a page yet stay unlinked. */
  to?:
    | typeof PATHS.CONVERSATIONS
    | typeof PATHS.CONVERSATIONS_ONBOARDING
    | typeof PATHS.CONVERSATIONS_CHANNELS
    | typeof PATHS.CONVERSATIONS_KNOWLEDGE_BASE
    | typeof PATHS.CONVERSATIONS_AGENTS
    | typeof PATHS.CONVERSATIONS_CONTACTS
    | typeof PATHS.CONVERSATIONS_AUTOMATION;
};

/** Figma “Omni Side Menu”. Each `to` is its own route under ConversationsLayout. */
const OMNI_TABS: ReadonlyArray<OmniTab> = [
  {
    id: 'onboarding',
    label: 'Get Started',
    icon: Category,
    to: PATHS.CONVERSATIONS_ONBOARDING,
  },
  { id: 'my-inbox', label: 'My Inbox', icon: Message, to: PATHS.CONVERSATIONS },
  // { id: 'ticlick-inbox', label: 'Ticlick Inbox' },
  {
    id: 'channels',
    label: 'Channels',
    icon: Data2,
    to: PATHS.CONVERSATIONS_CHANNELS,
  },
  {
    id: 'knowledge-base',
    label: 'Knowledge Base',
    icon: BrainIcon,
    to: PATHS.CONVERSATIONS_KNOWLEDGE_BASE,
  },
  {
    id: 'agents',
    label: 'My Agents',
    icon: EmojiHappy,
    to: PATHS.CONVERSATIONS_AGENTS,
  },
  {
    id: 'contacts',
    label: 'My Contacts',
    icon: ProfileCircle,
    to: PATHS.CONVERSATIONS_CONTACTS,
  },
  {
    id: 'automation',
    label: 'Automation',
    icon: Flash,
    to: PATHS.CONVERSATIONS_AUTOMATION,
  },
  // { id: 'reports', label: 'Reports', icon: Chart },
  // { id: 'broadcast', label: 'Broadcast', icon: Microscope },
  // { id: 'settings', label: 'Settings', icon: Setting2 },
];

function tabIsActive(tab: OmniTab, pathname: string): boolean {
  if (!tab.to) return false;
  if (tab.to === PATHS.CONVERSATIONS) {
    return (
      pathname.includes(PATHS.CONVERSATIONS) &&
      !OMNI_TABS.some(
        (other) =>
          other.to &&
          other.to !== PATHS.CONVERSATIONS &&
          pathname.includes(other.to)
      )
    );
  }
  return pathname.includes(tab.to);
}

export function OmniSideMenu() {
  const { pathname } = useLocation();

  return (
    <nav
      data-slot='omni-side-menu'
      aria-label='Inbox sections'
      className='flex h-full min-h-0 w-20 shrink-0 flex-col items-center gap-2 overflow-y-auto border-r border-primary-100 bg-white py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
    >
      {OMNI_TABS.map((tab) => {
        const Icon = tab.icon;
        const href = tab.to ? createRoute(tab.to) : undefined;
        const active = tabIsActive(tab, pathname);
        const className = cn(
          'flex size-12 shrink-0 items-center justify-center rounded-xl transition-colors',
          active
            ? 'border border-primary-200 bg-primary-100 text-primary-1000'
            : 'text-gray-700 hover:bg-primary-30'
        );
        const icon: ReactNode = Icon ? (
          <Icon
            size={24}
            variant={active ? 'Bold' : 'Linear'}
            aria-hidden='true'
          />
        ) : null;

        return (
          <Tooltip key={tab.id}>
            <TooltipTrigger asChild>
              {href ? (
                <Link
                  to={href}
                  aria-label={tab.label}
                  aria-current={active ? 'page' : undefined}
                  className={className}
                >
                  {icon}
                </Link>
              ) : (
                <button
                  type='button'
                  aria-label={tab.label}
                  className={className}
                >
                  {icon}
                </button>
              )}
            </TooltipTrigger>
            <TooltipContent side='right'>{tab.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}
